import { z } from 'zod';
import { heroAppIdSchema } from '../../common/app-registry.schema';
import { refreshSessionPlatformSchema } from '../refresh-session/refresh-session.model';
import { userMetaPermissionsSchema } from './user.meta.permissions.model';

/**
 * Cross-surface "last seen online" snapshot — shared by
 * `User.meta.lastSeenOnline` and `Org.meta.lastSeenOnline`.
 *
 * `platform` is optional at the schema level so pre-existing
 * `{ at, app }` docs (written before platform was added) still
 * parse; new writers always set it.
 */
export const lastSeenOnlineSchema = z.object({
  at: z.date(),
  app: heroAppIdSchema,
  platform: refreshSessionPlatformSchema.optional(),
});
export type LastSeenOnline = z.infer<typeof lastSeenOnlineSchema>;

/**
 * Presence entry for a single (app, platform) pair. Uniqueness is
 * `(app, platform)` — a user with `carrier_app` installed on both
 * an iPhone and an iPad reports as **one** entry (`app: 'carrier_app',
 * platform: 'ios'`), because per-device tracking is out of scope for
 * this signal layer. What matters:
 *
 * - `firstSeenAt` never rewinds. Set once on the first successful
 *   authed request from that `(app, platform)`.
 * - `lastSeenAt` bumps forward only. Bumped by the backend
 *   `PresenceInterceptor` on every authed request (server-throttled
 *   to at most once per 5 minutes per user × app × platform).
 * - `appVersion` / `osVersion` are best-effort snapshots reported by
 *   the client at heartbeat time. Powers "your app is out of date"
 *   / "you're on iOS 15, please update" prompts.
 * - `permissions` — per-(app, platform) OS grants. See the type
 *   above.
 */
export const userMetaPresenceAppSchema = z.object({
  app: heroAppIdSchema,
  /**
   * Platform this entry was reported from. Same enum the SDK sends
   * as `x-hero-platform` and the same enum `RefreshSession.device.platform`
   * stores — one vocabulary across the auth / session / presence layers.
 * WhatsApp agent turns stamp `whatsapp`; `unknown` is only an
 * unclassified header.
   */
  platform: refreshSessionPlatformSchema,
  /** When this user first authenticated from this (app, platform). Never rewinds. */
  firstSeenAt: z.date(),
  /** Last authed request from this (app, platform). Bumps forward only. */
  lastSeenAt: z.date(),
  /** Free-form app version string (e.g. "1.42.0"). Best-effort. */
  appVersion: z.string().trim().optional(),
  /** Free-form OS version string (e.g. "17.4.1", "14"). Best-effort. */
  osVersion: z.string().trim().optional(),
  /**
   * OS-level permission snapshot for this (app, platform). Reuses
   * the same shape as the (now-retired) top-level
   * `User.meta.permissions` block so old readers can be pointed at
   * the per-entry field without shape gymnastics.
   */
  permissions: userMetaPermissionsSchema.optional(),
});
export type UserMetaPresenceApp = z.infer<typeof userMetaPresenceAppSchema>;

/**
 * Cross-app presence surface. See `docs/ai/user-signals.md`.
 *
 * `apps[]` is append-mostly: writers upsert by `(app, platform)`,
 * replacing the matching entry rather than pushing duplicates.
 */
export const userMetaPresenceSchema = z.object({
  /** Per-(app, platform) first/last-seen tuples. Upserted by `(app, platform)`. */
  apps: z.array(userMetaPresenceAppSchema).default([]),
});
export type UserMetaPresence = z.infer<typeof userMetaPresenceSchema>;
